import React, { useEffect,useRef, useContext, useState, useMemo } from 'react';

// Markdown
import Markdown from 'markdown-to-jsx';
import { TextInput } from 'components/core/inputs';
import { Error, Heading } from 'components/core/typo';
import { Skeleton } from 'components/core/skeleton';

// Uils
import { helpOptions } from 'mdx';

// Hooks
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'hooks/useDebounce';

// API
import { updateTutor } from 'api/tutor';

// Context
import { TutorContext } from 'contexts/tutor';
import { Button } from '../ui/button';
import { AnimatePresence, motion } from 'framer-motion';

const facebookMarkdown = `
<div className="bg-gray-100 rounded-md p-3 mt-2">
🔍 <strong>Pour trouver votre URL de profil Facebook:</strong>

1. Connectez-vous à votre compte Facebook sur votre <strong>ordinateur</strong>. 
2. Cliquez sur votre nom en haut à droite de votre page d'accueil. Vous serez redirigé sur votre profil principal. 
3. Copiez le lien dans la barre d'adresse, car c'est l'URL de votre profil Facebook.
</div>`

const whyFacebookMarkdown = `
<div className="bg-gray-100 rounded-md p-3 mt-2">
👋 <strong>Pourquoi avons-nous besoin de votre URL Facebook?</strong>

Votre URL Facebook nous servira à vous mettre en contact plus facilement avec l’élève-athlète sur Messenger et à vous ajouter sur les groupes Facebook des tuteurs pour vous tenir informé plus rapidement des nouvelles importantes concernant le programme de tutorat. 
</div>`

export function ControlledFacebookURLInput({value, setValue, showErrorByDefault = true}){
    const {t} = useTranslation("tutor-form");
    const [forceChange, setForceChange] = useState(0);
    const showQuickSelection = useMemo(()=>{
        return !value;
    }, [value]);
    return <div>
            <Heading mediumTitle={t("facebook.short-label")} />
            <TextInput defaultValue={value} 
            forceValue={value}
            onChange={setValue}
            useLeftIcon={showErrorByDefault?true: false} 
            success={!!value}
            error={showErrorByDefault?!value: undefined}
            label={t("facebook.label")}
            forceChange={forceChange} 
            placeholder={t("facebook.placeholder")} 
            labelClassName="text-base"
            autoComplete="off"/>
            <AnimatePresence>{showQuickSelection && <motion.div 
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            exit={{opacity: 0, y: -10}}
            transition={{duration: 0.4}}
            className="flex gap-2 mt-2">
                {/* Quick selection: Je n'ai pas facebook. Je ne souhaite pas le communiquer. Je n'arrive pas à trouver mon profil. */}
                <Button variant="secondary" size="xs" onClick={(e)=>{e.preventDefault(); setForceChange(forceChange+1); setValue(t("facebook.no-facebook"))}}>{t("facebook.no-facebook")}</Button>
                <Button variant="secondary" size="xs" onClick={(e)=>{e.preventDefault(); setForceChange(forceChange+1); setValue(t("facebook.dont-want-to-share"))}}>{t("facebook.dont-want-to-share")}</Button>
                <Button variant="secondary" size="xs" onClick={(e)=>{e.preventDefault(); setForceChange(forceChange+1); setValue(t("facebook.cant-find-profile"))}}>{t("facebook.cant-find-profile")}</Button>
            </motion.div>}</AnimatePresence>
            <Markdown options={helpOptions}>{facebookMarkdown}</Markdown>
            <Markdown options={helpOptions}>{whyFacebookMarkdown}</Markdown>
        </div>
}
export function UncontrolledFacebookURLInput({defaultValue}){
    const [value, setValue] = useState(defaultValue || "");
    const debouncedValue = useDebounce(value, 300);
    const isMounted = useRef(false);
    useEffect(()=>{
        if (!isMounted.current){
            isMounted.current = true;
            return;
        }
        updateTutor({facebookAccount: value});
        return ()=>isMounted.current = true;
    }, [debouncedValue]);

    return <ControlledFacebookURLInput value={value} setValue={setValue}/>
}

export function FacebookURLInput(){
    const { tutor, error } = useContext(TutorContext);
    if (error){
        return <Error.Text {...error}/>
    }
    if (!tutor){
        return <Skeleton className="h-42"/>
    }
    return <UncontrolledFacebookURLInput defaultValue={tutor.facebook_account}/>
}